/**
 * StellungnahmenPage — Übersicht & Freigabe aller Stellungnahmen (SV-Büro)
 * Liest die Supabase-Views v_stellungnahme_uebersicht / v_stellungnahme_rueckfragen.
 * Phasen: neu · in_arbeit · in_freigabe · versendet · rechnung_offen · erledigt
 *
 * Karten sind klickbar → rechter Drawer "Freigabe & Erstellung" je Stellungnahme:
 *  - Tab-Leiste oben: Übersicht · Stellungnahme · Rechnung · Versand
 *  - Übersicht: kompakte Falldaten (View-Felder + akten-Nachladung)
 *  - Stellungnahme: Blöcke laden/bearbeiten/freigeben (über Service-Endpoint
 *    api/stellungnahme-abschnitte, da stellungnahme_abschnitte RLS aktiv + keine anon-Policy hat),
 *    Foto-Slots (api/stellungnahme-fotos), KI-Vorschlag & Zuordnen,
 *    Fortschritt + "Stellungnahme erstellen" (api/stellungnahme-generieren),
 *    "Weiter zur E-Mail" (api/stellungnahme-versand-vorbereiten)
 *  - Rechnung: rechnungen + rechnungspositionen zur Akte (quelle=stellungnahme)
 *  - Versand: aktueller E-Mail-Entwurf (email_drafts) zur Akte
 */
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Scale, AlertTriangle, MessageCircleQuestion, Clock, Gavel, RefreshCw, Filter, CheckCircle2, FileWarning, Flame,
  X, FileText, Image as ImageIcon, Send, Save, Loader2, Sparkles, ExternalLink, Mail,
  LayoutGrid, Receipt, Inbox, ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';

interface SnRow {
  id: string;
  akte_id: string | null;
  art: string | null;
  betreff: string | null;
  status: string | null;
  gericht: string | null;
  gericht_aktenzeichen: string | null;
  schriftsatzfrist: string | null;
  kuerzung_gesamt: number | null;
  eskalation: boolean | null;
  rueckfrage_offen: boolean | null;
  freigabe_bereit: boolean | null;
  liegezeit_tage: number | null;
  tage_bis_frist: number | null;
  phase: string;
  prioritaet: string | null;
  stellungnahme_nr: string | null;
  fehlablage_verdacht: number | null;
  kuerzungs_kandidaten: number | null;
}

interface RueckfrageRow {
  id: string;
  akte_id: string | null;
  betreff: string | null;
  status: string | null;
  schriftsatzfrist: string | null;
  eskalation: boolean | null;
  eskalation_grund: string | null;
  rueckfrage_text: string | null;
}

interface AbschnittRow {
  id: string;
  stellungnahme_id: string;
  reihenfolge: number;
  typ: string;
  titel: string | null;
  inhalt_html: string | null;
  freigegeben: boolean;
  freigegeben_am: string | null;
  bearbeitet: boolean;
  pflicht: boolean;
}

interface FreigabeStatus {
  bloecke_gesamt: number;
  bloecke_frei: number;
  pflicht_offen: number;
  alle_pflicht_frei: boolean;
}

interface FotoSlot {
  reihenfolge: number;
  gruppe: string | null;
  bildunterschrift: string | null;
  geprueft: boolean;
  vision_hinweis: string | null;
  dokument_id: string | null;
  dateiname: string | null;
  zugeordnet: boolean;
  url: string | null;
}

interface QuelleKandidat {
  dokument_id: string;
  dateiname: string | null;
  quelle_gruppe: string;
  vermuteter_slot: string | null;
  url: string | null;
}

// Falldaten aus der akten-Tabelle (per akte_id nachgeladen)
interface AkteRow {
  id: string;
  kunde_name: string | null;
  anwalt_name: string | null;
  kennzeichen: string | null;
  fahrzeug_marke: string | null;
  fahrzeug_modell: string | null;
  versicherung: string | null;
  schadennummer: string | null;
  kuerzung_betrag: number | null;
  kuerzung_grund: string | null;
  schadentag: string | null;
}

interface RechnungRow {
  id: string;
  rechnungs_nr: string | null;
  rechnungsdatum: string | null;
  faellig_am: string | null;
  status: string | null;
  netto_betrag: number | null;
  mwst_betrag: number | null;
  brutto_betrag: number | null;
}

interface RechnungPosition {
  id: string;
  position_nr: number | null;
  bezeichnung: string | null;
  menge: number | null;
  einheit: string | null;
  einzelpreis_netto: number | null;
  betrag_netto: number | null;
}

interface AnalysePosition {
  reihenfolge: number | null;
  bezeichnung: string | null;
  betrag_gutachten: number | null;
  betrag_kuerzung: number | null;
  kuerzung_grund: string | null;
  begruendung: string | null;
  begruendbar: boolean | null;
  konfidenz: string | null;
  fundstelle: string | null;
}

interface EmailDraftRow {
  id: string;
  recipient: string | null;
  cc: string[] | null;
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
  review_note: string | null;
  status: string | null;
  created_at: string | null;
}

const PHASEN: { id: string; label: string }[] = [
  { id: 'neu', label: 'Neu' },
  { id: 'in_arbeit', label: 'In Arbeit' },
  { id: 'in_freigabe', label: 'In Freigabe' },
  { id: 'versendet', label: 'Versendet' },
  { id: 'rechnung_offen', label: 'Rechnung offen' },
  { id: 'erledigt', label: 'Erledigt' },
];

function fristAmpel(tage: number | null): string {
  if (tage === null || tage === undefined) return 'bg-gray-100 text-gray-500';
  if (tage < 0) return 'bg-red-600 text-white';
  if (tage <= 3) return 'bg-red-100 text-red-700';
  if (tage <= 7) return 'bg-amber-100 text-amber-700';
  return 'bg-gray-100 text-gray-600';
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-DE');
}

function fmtEuro(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function val(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

// Kompaktes Label/Wert-Paar fürs Definition-Grid
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="text-[13px] text-gray-800">{value}</dd>
    </div>
  );
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token ?? '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

const DRAWER_TABS: { id: string; label: string }[] = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'analyse', label: 'Analyse' },
  { id: 'stellungnahme', label: 'Stellungnahme' },
  { id: 'rechnung', label: 'Rechnung' },
  { id: 'versand', label: 'Versand' },
];

// ── Drawer ───────────────────────────────────────────────────────────────────

function FreigabeDrawer({ sn, onClose }: { sn: SnRow; onClose: () => void }) {
  const [tab, setTab] = useState('uebersicht');
  const [abschnitte, setAbschnitte] = useState<AbschnittRow[]>([]);
  const [status, setStatus] = useState<FreigabeStatus | null>(null);
  const [slots, setSlots] = useState<FotoSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [galleryFor, setGalleryFor] = useState<number | null>(null);
  const [gallery, setGallery] = useState<QuelleKandidat[]>([]);
  const [mailStep, setMailStep] = useState<any | null>(null);
  const [mailLoading, setMailLoading] = useState(false);
  // Übersicht/Rechnung/Versand — direkte Supabase-Queries je Tab
  const [akte, setAkte] = useState<AkteRow | null>(null);
  const [rechnung, setRechnung] = useState<RechnungRow | null>(null);
  const [positionen, setPositionen] = useState<RechnungPosition[]>([]);
  const [rechnungLoading, setRechnungLoading] = useState(true);
  // Analyse-Tab — Kürzungspositionen (eigener State, kollidiert nicht mit Rechnungs-positionen)
  const [analysePositionen, setAnalysePositionen] = useState<AnalysePosition[]>([]);
  const [analyseLoading, setAnalyseLoading] = useState(true);
  // "Jetzt vorbereiten" (Stellungnahme-Tab) — Vorbereitungs-Flag
  const [vorbereiten, setVorbereiten] = useState(false);
  const [emailDraft, setEmailDraft] = useState<EmailDraftRow | null>(null);
  const [versandLoading, setVersandLoading] = useState(true);
  // Presentation-only: triggert die Slide-in-Animation nach dem Mounten
  const [shown, setShown] = useState(false);
  useEffect(() => { const t = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(t); }, []);

  async function loadBloecke() {
    setLoading(true);
    try {
      const h = await authHeaders();
      const r = await fetch(`/api/stellungnahme-abschnitte?stellungnahme_id=${sn.id}`, { headers: h });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Fehler beim Laden');
      const rows = (j.abschnitte as AbschnittRow[]) ?? [];
      setAbschnitte(rows);
      setStatus(j.status ?? null);
      const d: Record<string, string> = {};
      rows.forEach((a) => { d[a.id] = a.inhalt_html ?? ''; });
      setDrafts(d);
    } catch (e: any) {
      toast.error(e?.message ?? 'Blöcke konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }

  async function loadSlots() {
    try {
      const h = await authHeaders();
      const r = await fetch(`/api/stellungnahme-fotos?action=slots&stellungnahme_id=${sn.id}`, { headers: h });
      const j = await r.json();
      if (r.ok) setSlots((j.slots as FotoSlot[]) ?? []);
    } catch { /* still optional */ }
  }

  // Falldaten der Akte (Übersicht-Tab) bei Drawer-Öffnung nachladen
  async function loadAkte() {
    if (!sn.akte_id) { setAkte(null); return; }
    try {
      const { data } = await supabase
        .from('akten')
        .select('id, kunde_name, anwalt_name, kennzeichen, fahrzeug_marke, fahrzeug_modell, versicherung, schadennummer, kuerzung_betrag, kuerzung_grund, schadentag')
        .eq('id', sn.akte_id)
        .maybeSingle();
      setAkte((data as AkteRow) ?? null);
    } catch { /* Übersicht zeigt sonst nur View-Felder */ }
  }

  // Rechnung + Positionen zur Akte (Rechnung-Tab)
  async function loadRechnung() {
    setRechnungLoading(true);
    try {
      if (!sn.akte_id) { setRechnung(null); setPositionen([]); return; }
      const { data: rData } = await supabase
        .from('rechnungen')
        .select('*')
        .eq('akte_id', sn.akte_id)
        .eq('quelle', 'stellungnahme')
        .order('rechnungsdatum', { ascending: false });
      const r = (rData as RechnungRow[] | null)?.[0] ?? null;
      setRechnung(r);
      if (r?.id) {
        const { data: pData } = await supabase
          .from('rechnungspositionen')
          .select('*')
          .eq('rechnung_id', r.id)
          .order('position_nr');
        setPositionen((pData as RechnungPosition[]) ?? []);
      } else {
        setPositionen([]);
      }
    } catch { /* dezenter Hinweis im Tab */ } finally {
      setRechnungLoading(false);
    }
  }

  // E-Mail-Entwurf zur Akte (Versand-Tab)
  async function loadEmailDraft() {
    setVersandLoading(true);
    try {
      if (!sn.akte_id) { setEmailDraft(null); return; }
      const { data } = await supabase
        .from('email_drafts')
        .select('*')
        .eq('akte_id', sn.akte_id)
        .order('created_at', { ascending: false })
        .limit(1);
      setEmailDraft((data as EmailDraftRow[] | null)?.[0] ?? null);
    } catch { /* dezenter Hinweis im Tab */ } finally {
      setVersandLoading(false);
    }
  }

  // Kürzungspositionen zur Stellungnahme (Analyse-Tab)
  async function loadAnalyse() {
    setAnalyseLoading(true);
    try {
      const { data } = await supabase
        .from('stellungnahme_positionen')
        .select('reihenfolge, bezeichnung, betrag_gutachten, betrag_kuerzung, kuerzung_grund, begruendung, begruendbar, konfidenz, fundstelle')
        .eq('stellungnahme_id', sn.id)
        .order('reihenfolge', { ascending: true });
      setAnalysePositionen((data as AnalysePosition[]) ?? []);
    } catch { /* dezenter Fallback im Tab */ } finally {
      setAnalyseLoading(false);
    }
  }

  // "Jetzt vorbereiten" — Service-Endpoint, danach Blöcke neu laden
  async function vorbereitenStarten() {
    setVorbereiten(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/stellungnahme-vorbereiten', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token ?? ''}` },
        body: JSON.stringify({ stellungnahme_id: sn.id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Vorbereitung fehlgeschlagen');
      toast.success('Stellungnahme vorbereitet');
      await loadBloecke();
    } catch (e: any) {
      toast.error(e?.message ?? 'Vorbereitung fehlgeschlagen');
    } finally {
      setVorbereiten(false);
    }
  }

  useEffect(() => {
    loadBloecke(); loadSlots(); loadAkte(); loadRechnung(); loadEmailDraft(); loadAnalyse();
    /* eslint-disable-next-line */
  }, [sn.id]);

  const sorted = useMemo(
    () => [...abschnitte].sort((a, b) => (a.typ === 'daten' ? -1 : b.typ === 'daten' ? 1 : a.reihenfolge - b.reihenfolge)),
    [abschnitte],
  );

  async function saveBlock(a: AbschnittRow) {
    setSavingId(a.id);
    try {
      const h = await authHeaders();
      const r = await fetch('/api/stellungnahme-abschnitte', {
        method: 'POST', headers: h,
        body: JSON.stringify({ stellungnahme_id: sn.id, id: a.id, inhalt_html: drafts[a.id] ?? '' }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Speichern fehlgeschlagen');
      if (j.status) setStatus(j.status);
      setAbschnitte((prev) => prev.map((x) => (x.id === a.id ? { ...x, inhalt_html: drafts[a.id] ?? '', bearbeitet: true } : x)));
      toast.success('Block gespeichert');
    } catch (e: any) {
      toast.error(e?.message ?? 'Fehler beim Speichern');
    } finally {
      setSavingId(null);
    }
  }

  async function toggleFreigabe(a: AbschnittRow) {
    const next = !a.freigegeben;
    try {
      const h = await authHeaders();
      const r = await fetch('/api/stellungnahme-abschnitte', {
        method: 'POST', headers: h,
        body: JSON.stringify({ stellungnahme_id: sn.id, id: a.id, freigegeben: next }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Freigabe fehlgeschlagen');
      if (j.status) setStatus(j.status);
      setAbschnitte((prev) => prev.map((x) => (x.id === a.id ? { ...x, freigegeben: next, freigegeben_am: next ? new Date().toISOString() : null } : x)));
    } catch (e: any) {
      toast.error(e?.message ?? 'Fehler bei Freigabe');
    }
  }

  async function visionVorschlag(reihenfolge: number) {
    try {
      const h = await authHeaders();
      const r = await fetch('/api/stellungnahme-fotos', {
        method: 'POST', headers: h,
        body: JSON.stringify({ action: 'vision-vorschlag', stellungnahme_id: sn.id, reihenfolge }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'KI-Vorschlag fehlgeschlagen');
      toast.success(j.vorschlag ? `KI-Vorschlag übernommen (${j.konfidenz})` : (j.hinweis || 'Kein Vorschlag'));
      loadSlots();
    } catch (e: any) {
      toast.error(e?.message ?? 'KI-Vorschlag fehlgeschlagen');
    }
  }

  async function openGallery(reihenfolge: number) {
    setGalleryFor(reihenfolge);
    setGallery([]);
    try {
      const h = await authHeaders();
      const r = await fetch(`/api/stellungnahme-fotos?action=quellen&stellungnahme_id=${sn.id}`, { headers: h });
      const j = await r.json();
      if (r.ok) setGallery((j.kandidaten as QuelleKandidat[]) ?? []);
    } catch { /* ignore */ }
  }

  async function zuordnen(reihenfolge: number, dokument_id: string) {
    try {
      const h = await authHeaders();
      const r = await fetch('/api/stellungnahme-fotos', {
        method: 'POST', headers: h,
        body: JSON.stringify({ action: 'zuordnen', stellungnahme_id: sn.id, reihenfolge, dokument_id }),
      });
      if (!r.ok) { const j = await r.json(); throw new Error(j.error || 'Zuordnen fehlgeschlagen'); }
      toast.success('Foto zugeordnet');
      setGalleryFor(null);
      loadSlots();
    } catch (e: any) {
      toast.error(e?.message ?? 'Zuordnen fehlgeschlagen');
    }
  }

  async function generieren() {
    setGenerating(true);
    try {
      const h = await authHeaders();
      const r = await fetch('/api/stellungnahme-generieren', {
        method: 'POST', headers: h, body: JSON.stringify({ stellungnahme_id: sn.id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Erstellung fehlgeschlagen');
      setPdfUrl(j.signed_url ?? null);
      toast.success('Stellungnahme erstellt');
    } catch (e: any) {
      toast.error(e?.message ?? 'Erstellung fehlgeschlagen');
    } finally {
      setGenerating(false);
    }
  }

  async function weiterZurEmail() {
    setMailLoading(true);
    try {
      const h = await authHeaders();
      const r = await fetch('/api/stellungnahme-versand-vorbereiten', {
        method: 'POST', headers: h, body: JSON.stringify({ stellungnahme_id: sn.id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'E-Mail-Vorbereitung fehlgeschlagen');
      setMailStep(j);
      toast.success('E-Mail-Entwurf vorbereitet');
    } catch (e: any) {
      toast.error(e?.message ?? 'E-Mail-Vorbereitung fehlgeschlagen');
    } finally {
      setMailLoading(false);
    }
  }

  const frei = status?.bloecke_frei ?? abschnitte.filter((a) => a.freigegeben).length;
  const gesamt = status?.bloecke_gesamt ?? abschnitte.length;
  const allePflichtFrei = status?.alle_pflicht_frei ?? false;
  const pct = gesamt > 0 ? Math.round((frei / gesamt) * 100) : 0;

  const fahrzeug = [akte?.fahrzeug_marke, akte?.fahrzeug_modell].filter(Boolean).join(' ') || null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${shown ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
      <aside
        style={{ width: '80vw', maxWidth: '1100px' }}
        className={`relative h-full max-md:!w-screen bg-white shadow-xl flex flex-col transition-transform duration-300 ease-out ${shown ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="sticky top-0 z-20 flex items-start justify-between gap-4 px-8 py-5 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
          <div>
            <p className="text-[11px] font-mono text-gray-400">{sn.stellungnahme_nr}</p>
            <h2 className="text-lg font-bold text-gray-900 leading-snug">Freigabe &amp; Erstellung</h2>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{sn.betreff}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors"><X className="w-5 h-5" /></button>
        </div>

        {/* Tab-Leiste */}
        <div className="px-8 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-7">
            {DRAWER_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`relative py-3 text-[13px] font-medium transition-colors ${tab === t.id ? 'text-[#9B1C1C]' : 'text-gray-400 hover:text-gray-600'}`}>
                {t.label}
                {tab === t.id && <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-[#9B1C1C]" />}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab: Übersicht ─────────────────────────────────────────── */}
        {tab === 'uebersicht' && (
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7">
            <section>
              <div className="flex items-center gap-2 mb-3">
                <LayoutGrid className="w-4 h-4 text-gray-300" />
                <h3 className="text-[13px] font-semibold text-gray-800">Status &amp; Frist</h3>
              </div>
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                <Field label="Betreff" value={val(sn.betreff)} />
                <Field label="Stellungnahme-Nr." value={val(sn.stellungnahme_nr)} />
                <Field label="Status" value={val(sn.status)} />
                <Field label="Phase" value={val(sn.phase)} />
                <Field label="Priorität" value={val(sn.prioritaet)} />
                <Field label="Liegezeit" value={sn.liegezeit_tage !== null && sn.liegezeit_tage !== undefined ? `${sn.liegezeit_tage} Tage` : '—'} />
                <Field label="Schriftsatzfrist" value={fmtDate(sn.schriftsatzfrist)} />
                <Field
                  label="Tage bis Frist"
                  value={sn.tage_bis_frist !== null && sn.tage_bis_frist !== undefined
                    ? <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${fristAmpel(sn.tage_bis_frist)}`}><Clock className="w-3 h-3" />{sn.tage_bis_frist < 0 ? `${Math.abs(sn.tage_bis_frist)}T überfällig` : `${sn.tage_bis_frist}T`}</span>
                    : '—'}
                />
                <Field label="Schadentag" value={fmtDate(akte?.schadentag)} />
              </dl>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <FileWarning className="w-4 h-4 text-gray-300" />
                <h3 className="text-[13px] font-semibold text-gray-800">Was wurde gekürzt</h3>
              </div>
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                <Field label="Kürzung gesamt" value={fmtEuro(sn.kuerzung_gesamt)} />
                <Field label="Kürzungsbetrag (Akte)" value={fmtEuro(akte?.kuerzung_betrag)} />
                <div className="col-span-2 md:col-span-3">
                  <Field label="Kürzungsgrund" value={val(akte?.kuerzung_grund)} />
                </div>
              </dl>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Gavel className="w-4 h-4 text-gray-300" />
                <h3 className="text-[13px] font-semibold text-gray-800">Gericht / Aktenzeichen</h3>
              </div>
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                <Field label="Gericht" value={val(sn.gericht)} />
                <Field label="Aktenzeichen" value={val(sn.gericht_aktenzeichen)} />
              </dl>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Scale className="w-4 h-4 text-gray-300" />
                <h3 className="text-[13px] font-semibold text-gray-800">Beteiligte</h3>
              </div>
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                <Field label="Kunde" value={val(akte?.kunde_name)} />
                <Field label="Rechtsanwalt" value={val(akte?.anwalt_name)} />
                <Field label="Versicherung" value={val(akte?.versicherung)} />
                <Field label="Schadennummer" value={val(akte?.schadennummer)} />
                <Field label="Kennzeichen" value={val(akte?.kennzeichen)} />
                <Field label="Fahrzeug" value={val(fahrzeug)} />
              </dl>
            </section>
          </div>
        )}

        {/* ── Tab: Analyse ───────────────────────────────────────────── */}
        {tab === 'analyse' && (
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-5">
            {/* Summenzeile */}
            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/60 px-5 py-3">
              <div className="flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-gray-400" />
                <span className="text-[13px] font-semibold text-gray-800">{analysePositionen.length} Positionen</span>
              </div>
              <span className="text-[13px] text-gray-600">
                Summe Kürzung: <span className="font-semibold text-gray-900">{fmtEuro(analysePositionen.reduce((s, p) => s + (p.betrag_kuerzung ?? 0), 0))}</span>
              </span>
            </div>

            {analyseLoading ? (
              <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />)}</div>
            ) : analysePositionen.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-6 py-10 text-center">
                <ListChecks className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Noch keine Positionen analysiert.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {analysePositionen.map((p, i) => (
                  <div key={p.reihenfolge ?? i} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
                    {/* Kopf */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 min-w-0">
                        {p.reihenfolge !== null && p.reihenfolge !== undefined && (
                          <span className="text-[10px] font-mono text-gray-400 mt-0.5">#{p.reihenfolge}</span>
                        )}
                        <span className="text-[14px] font-semibold text-gray-900 break-words">{p.bezeichnung ?? '—'}</span>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        {p.begruendbar === true ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
                            Kürzung nicht haltbar / begründbar
                          </span>
                        ) : p.begruendbar === false ? (
                          <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">
                            noch zu prüfen
                          </span>
                        ) : null}
                        {p.konfidenz && (
                          <span className="text-[10px] text-gray-400">Konfidenz: {p.konfidenz}</span>
                        )}
                      </div>
                    </div>

                    {/* Beträge */}
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[12px]">
                      <span className="text-gray-500">Gutachten: <span className="font-medium text-gray-800">{fmtEuro(p.betrag_gutachten)}</span></span>
                      <span className="text-gray-500">Gekürzt: <span className="font-medium text-gray-800">{fmtEuro(p.betrag_kuerzung)}</span></span>
                    </div>

                    {/* Kürzungsgrund Gegenseite */}
                    <div className="space-y-1">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Kürzungsgrund (Gegenseite)</p>
                      <p className="text-[13px] text-gray-700 break-words">{val(p.kuerzung_grund)}</p>
                    </div>

                    {/* Unsere Einschätzung */}
                    <div className="space-y-1">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Unsere Einschätzung</p>
                      {p.begruendung ? (
                        <p className="text-[13px] text-gray-800 break-words">{p.begruendung}</p>
                      ) : (
                        <p className="text-[12px] italic text-gray-400">— noch keine Begründung —</p>
                      )}
                    </div>

                    {p.fundstelle && (
                      <p className="text-[10px] text-gray-400">Fundstelle: {p.fundstelle}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Stellungnahme ─────────────────────────────────────── */}
        {tab === 'stellungnahme' && (
          <>
            {/* Fortschritt */}
            <div className="px-8 py-4 border-b border-gray-200 bg-gray-50/60">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
                <span className="font-medium">Freigabe-Fortschritt</span>
                <span className="font-semibold text-gray-800">{frei}/{gesamt} Blöcke</span>
              </div>
              <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              {!allePflichtFrei && (status?.pflicht_offen ?? 0) > 0 && (
                <p className="text-[11px] text-amber-600 mt-1.5">{status?.pflicht_offen} Pflicht-Block(e) noch offen</p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-4">
              {loading ? (
                <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />)}</div>
              ) : (
                <>
                  {/* "Jetzt vorbereiten" — wenn noch keine Blöcke vorhanden sind */}
                  {sorted.length === 0 && (
                    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-6 py-10 text-center">
                      <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 mb-4">Diese Stellungnahme ist noch nicht vorbereitet.</p>
                      <button
                        onClick={vorbereitenStarten}
                        disabled={vorbereiten}
                        className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-[#9B1C1C] hover:bg-[#7f1818] rounded-xl px-5 py-2.5 transition disabled:opacity-60">
                        {vorbereiten ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {vorbereiten ? 'Wird vorbereitet …' : 'Jetzt vorbereiten'}
                      </button>
                    </div>
                  )}

                  {sorted.map((a) => (
                    <div key={a.id} className={`rounded-xl border p-5 ${a.freigegeben ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[10px] font-mono text-gray-400">#{a.reihenfolge}</span>
                          <span className="text-[13px] font-semibold text-gray-900 truncate">{a.titel ?? a.typ}</span>
                          {a.pflicht && <span className="text-[9px] uppercase tracking-wide text-gray-400">Pflicht</span>}
                        </div>
                        <button
                          onClick={() => toggleFreigabe(a)}
                          className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg transition ${a.freigegeben ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> {a.freigegeben ? 'Freigegeben' : 'Freigeben'}
                        </button>
                      </div>
                      <textarea
                        value={drafts[a.id] ?? ''}
                        onChange={(e) => setDrafts((p) => ({ ...p, [a.id]: e.target.value }))}
                        rows={a.typ === 'daten' ? 3 : 5}
                        className="w-full text-[13px] text-gray-700 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#9B1C1C]/30 resize-y"
                        placeholder="Inhalt (HTML erlaubt)…"
                      />
                      <div className="flex items-center justify-end mt-2">
                        <button
                          onClick={() => saveBlock(a)}
                          disabled={savingId === a.id || (drafts[a.id] ?? '') === (a.inhalt_html ?? '')}
                          className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:opacity-40 px-2 py-1">
                          {savingId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Speichern
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Foto-Bereich */}
                  <div className="pt-2">
                    <div className="flex items-center gap-2 mb-2">
                      <ImageIcon className="w-4 h-4 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-800">Abbildungen</h3>
                    </div>
                    {slots.length === 0 ? (
                      <p className="text-xs text-gray-400">Keine Bild-Slots definiert.</p>
                    ) : (
                      <div className="space-y-2">
                        {slots.map((s) => (
                          <div key={s.reihenfolge} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3.5">
                            <div className="w-16 h-12 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                              {s.url ? <img src={s.url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-gray-300" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[12px] font-medium text-gray-800 truncate">Abb. {s.reihenfolge}{s.bildunterschrift ? `: ${s.bildunterschrift}` : ''}</p>
                              {s.vision_hinweis && <p className="text-[10px] text-amber-600 truncate">{s.vision_hinweis}</p>}
                              {!s.zugeordnet && <p className="text-[10px] text-gray-400">nicht zugeordnet</p>}
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <button onClick={() => visionVorschlag(s.reihenfolge)} className="flex items-center gap-1 text-[11px] font-medium text-gray-600 hover:text-[#9B1C1C] px-2 py-1 rounded-lg hover:bg-gray-50">
                                <Sparkles className="w-3.5 h-3.5" /> KI
                              </button>
                              <button onClick={() => openGallery(s.reihenfolge)} className="text-[11px] font-medium text-gray-600 hover:text-gray-900 px-2 py-1 rounded-lg hover:bg-gray-50">
                                Zuordnen
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Aktionsleiste */}
            <div className="border-t border-gray-200 px-8 py-4 space-y-2.5 bg-white">
              {pdfUrl && (
                <a href={pdfUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-sm font-medium text-[#1c2e52] hover:underline">
                  <FileText className="w-4 h-4" /> PDF-Vorschau öffnen <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              {!pdfUrl ? (
                <button
                  onClick={generieren}
                  disabled={!allePflichtFrei || generating}
                  className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:text-gray-400 rounded-xl py-2.5 transition">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {generating ? 'Wird erstellt…' : 'Stellungnahme erstellen'}
                </button>
              ) : (
                <button
                  onClick={weiterZurEmail}
                  disabled={mailLoading}
                  className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white bg-[#9B1C1C] hover:bg-[#7f1818] rounded-xl py-2.5 transition disabled:opacity-60">
                  {mailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Weiter zur E-Mail
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Tab: Rechnung ──────────────────────────────────────────── */}
        {tab === 'rechnung' && (
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {rechnungLoading ? (
              <div className="space-y-3">{[0, 1].map((i) => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>
            ) : !rechnung ? (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-6 py-10 text-center">
                <Receipt className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Noch keine Rechnung – wird bei Freigabe erzeugt.</p>
              </div>
            ) : (
              <>
                <section className="rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Receipt className="w-4 h-4 text-gray-300" />
                    <h3 className="text-[13px] font-semibold text-gray-800">Rechnung</h3>
                  </div>
                  <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                    <Field label="Rechnungsnummer" value={val(rechnung.rechnungs_nr)} />
                    <Field label="Datum" value={fmtDate(rechnung.rechnungsdatum)} />
                    <Field label="Fällig" value={fmtDate(rechnung.faellig_am)} />
                    <Field label="Status" value={val(rechnung.status)} />
                    <Field label="Netto" value={fmtEuro(rechnung.netto_betrag)} />
                    <Field label="MwSt." value={fmtEuro(rechnung.mwst_betrag)} />
                    <Field label="Brutto" value={<span className="font-semibold">{fmtEuro(rechnung.brutto_betrag)}</span>} />
                  </dl>
                </section>

                {positionen.length > 0 && (
                  <section>
                    <h4 className="text-[13px] font-semibold text-gray-800 mb-2">Positionen</h4>
                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                      <table className="w-full text-[12px]">
                        <thead>
                          <tr className="bg-gray-50 text-gray-400 text-left">
                            <th className="font-medium px-4 py-2 w-8">#</th>
                            <th className="font-medium px-4 py-2">Bezeichnung</th>
                            <th className="font-medium px-4 py-2 text-right">Menge</th>
                            <th className="font-medium px-4 py-2 text-right">Einzelpreis</th>
                            <th className="font-medium px-4 py-2 text-right">Betrag</th>
                          </tr>
                        </thead>
                        <tbody>
                          {positionen.map((p) => (
                            <tr key={p.id} className="border-t border-gray-100">
                              <td className="px-4 py-2 text-gray-400 font-mono">{p.position_nr ?? '—'}</td>
                              <td className="px-4 py-2 text-gray-800">{val(p.bezeichnung)}</td>
                              <td className="px-4 py-2 text-right text-gray-600">{p.menge !== null && p.menge !== undefined ? `${p.menge}${p.einheit ? ` ${p.einheit}` : ''}` : '—'}</td>
                              <td className="px-4 py-2 text-right text-gray-600">{fmtEuro(p.einzelpreis_netto)}</td>
                              <td className="px-4 py-2 text-right text-gray-800">{fmtEuro(p.betrag_netto)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Tab: Versand ───────────────────────────────────────────── */}
        {tab === 'versand' && (
          <>
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-5">
              {versandLoading ? (
                <div className="space-y-3">{[0, 1].map((i) => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>
              ) : !emailDraft ? (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-6 py-10 text-center">
                  <Inbox className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Noch kein Versand-Entwurf – wird nach Erstellung vorbereitet.</p>
                </div>
              ) : (
                <>
                  <section className="rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Mail className="w-4 h-4 text-gray-300" />
                      <h3 className="text-[13px] font-semibold text-gray-800">E-Mail-Entwurf</h3>
                      {emailDraft.status && <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">{emailDraft.status}</span>}
                    </div>
                    <dl className="grid grid-cols-[88px_1fr] gap-y-2.5 gap-x-3 text-[13px]">
                      <dt className="text-[11px] text-gray-400 pt-0.5">An</dt><dd className="text-gray-800">{val(emailDraft.recipient)}</dd>
                      <dt className="text-[11px] text-gray-400 pt-0.5">CC</dt><dd className="text-gray-800">{(emailDraft.cc ?? []).join(', ') || '—'}</dd>
                      <dt className="text-[11px] text-gray-400 pt-0.5">Betreff</dt><dd className="text-gray-800 font-medium">{val(emailDraft.subject)}</dd>
                    </dl>
                  </section>

                  <section>
                    <h4 className="text-[13px] font-semibold text-gray-800 mb-2">Nachricht</h4>
                    {emailDraft.body_html ? (
                      <iframe
                        title="E-Mail-Vorschau"
                        sandbox=""
                        srcDoc={emailDraft.body_html}
                        className="w-full h-72 rounded-xl border border-gray-200 bg-white"
                      />
                    ) : (
                      <pre className="rounded-xl border border-gray-200 p-4 whitespace-pre-wrap text-[13px] text-gray-700 max-h-72 overflow-y-auto font-sans">{emailDraft.body_text || '—'}</pre>
                    )}
                  </section>

                  {emailDraft.review_note && (
                    <section>
                      <h4 className="text-[13px] font-semibold text-gray-800 mb-2">Anhänge / Hinweis</h4>
                      <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 whitespace-pre-wrap text-[12px] text-gray-600">{emailDraft.review_note}</div>
                    </section>
                  )}
                </>
              )}
            </div>

            <div className="border-t border-gray-200 px-8 py-4 bg-white">
              <button
                onClick={() => toast.info('Versand erfolgt im E-Mail-Modul – dort senden.')}
                className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white bg-[#9B1C1C] hover:bg-[#7f1818] rounded-xl py-2.5 transition">
                <Mail className="w-4 h-4" /> Zur E-Mail-Freigabe
              </button>
              <p className="text-[11px] text-gray-400 text-center mt-2">Hinweis: Der Versand erfolgt im E-Mail-Modul.</p>
            </div>
          </>
        )}

        {/* Galerie-Overlay */}
        {galleryFor !== null && (
          <div className="absolute inset-0 z-30 bg-white flex flex-col">
            <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Foto für Abb. {galleryFor} wählen</h3>
              <button onClick={() => setGalleryFor(null)} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 grid grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.length === 0 && <p className="col-span-3 lg:col-span-4 text-xs text-gray-400 text-center py-8">Keine Kandidaten gefunden.</p>}
              {gallery.map((k) => (
                <button key={k.dokument_id} onClick={() => zuordnen(galleryFor, k.dokument_id)} className="group rounded-xl border border-gray-200 overflow-hidden hover:border-[#9B1C1C] hover:shadow-sm transition text-left">
                  <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center">
                    {k.url ? <img src={k.url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-gray-300" />}
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-[10px] font-medium text-gray-700 truncate">{k.dateiname ?? k.dokument_id}</p>
                    <p className="text-[9px] text-gray-400 truncate">{k.quelle_gruppe}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* E-Mail-Schritt-Overlay */}
        {mailStep && (
          <div className="absolute inset-0 z-30 bg-white flex flex-col">
            <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">E-Mail-Entwurf prüfen</h3>
              <button onClick={() => setMailStep(null)} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-8 space-y-3 text-sm">
              <div className="grid grid-cols-[80px_1fr] gap-y-1.5 gap-x-3">
                <span className="text-gray-400 text-xs">An</span><span className="text-gray-800">{mailStep.recipient || '—'}</span>
                <span className="text-gray-400 text-xs">CC</span><span className="text-gray-800">{(mailStep.cc || []).join(', ') || '—'}</span>
                <span className="text-gray-400 text-xs">Betreff</span><span className="text-gray-800 font-medium">{mailStep.subject}</span>
              </div>
              <div className="rounded-xl border border-gray-200 p-4 whitespace-pre-wrap text-[13px] text-gray-700 max-h-64 overflow-y-auto">{mailStep.body_text}</div>
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">Anhänge</p>
                <div className="flex flex-wrap gap-1.5">
                  {(mailStep.anhaenge || []).map((a: string) => (
                    <span key={a} className="text-[11px] bg-gray-100 text-gray-600 rounded-lg px-2 py-0.5 flex items-center gap-1"><FileText className="w-3 h-3" />{a}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 px-8 py-4 flex items-center gap-2.5">
              <button
                onClick={() => { toast.success('Entwurf bleibt zur Freigabe im E-Mail-Modul'); setMailStep(null); onClose(); }}
                className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold text-[#9B1C1C] border border-[#9B1C1C]/30 hover:bg-[#9B1C1C]/5 rounded-xl py-2.5">
                <Mail className="w-4 h-4" /> Zur E-Mail-Freigabe
              </button>
              <button
                onClick={() => toast.info('Versand erfolgt im E-Mail-Modul – hier nur Vorbereitung.')}
                className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gray-300 cursor-not-allowed rounded-xl py-2.5">
                <Send className="w-4 h-4" /> Jetzt senden
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

export default function StellungnahmenPage() {
  const [rows, setRows] = useState<SnRow[]>([]);
  const [rueckfragen, setRueckfragen] = useState<RueckfrageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [artFilter, setArtFilter] = useState('');
  const [nurDringend, setNurDringend] = useState(false);
  const [suche, setSuche] = useState('');
  const [tab, setTab] = useState<'uebersicht' | 'rueckfragen'>('uebersicht');
  const [selected, setSelected] = useState<SnRow | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [u, r] = await Promise.all([
        supabase.from('v_stellungnahme_uebersicht').select('*'),
        supabase.from('v_stellungnahme_rueckfragen').select('*'),
      ]);
      if (u.error) throw u.error;
      setRows((u.data as SnRow[]) ?? []);
      setRueckfragen((r.data as RueckfrageRow[]) ?? []);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const gefiltert = useMemo(() => {
    return rows.filter(r => {
      if (artFilter && r.art !== artFilter) return false;
      if (nurDringend && r.prioritaet !== 'dringend') return false;
      if (suche) {
        const s = suche.toLowerCase();
        if (!(`${r.betreff ?? ''} ${r.gericht ?? ''} ${r.gericht_aktenzeichen ?? ''} ${r.stellungnahme_nr ?? ''}`.toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [rows, artFilter, nurDringend, suche]);

  const byPhase = (phase: string) => gefiltert.filter(r => r.phase === phase);
  const dringendCount = rows.filter(r => r.prioritaet === 'dringend').length;

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#9B1C1C]/10 flex items-center justify-center">
            <Scale className="w-5 h-5 text-[#9B1C1C]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Stellungnahmen</h1>
            <p className="text-sm text-gray-500">{rows.length} gesamt · {dringendCount} dringend · {rueckfragen.length} offene Rückfragen</p>
          </div>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 text-sm font-medium text-white bg-[#9B1C1C] hover:bg-[#7f1818] px-3 py-2 rounded-lg disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </button>
      </div>

      <div className="flex items-center gap-4 border-b border-gray-200">
        <button onClick={() => setTab('uebersicht')}
          className={`pb-2 text-sm font-medium ${tab === 'uebersicht' ? 'text-[#9B1C1C] border-b-2 border-[#9B1C1C]' : 'text-gray-500'}`}>
          Übersicht
        </button>
        <button onClick={() => setTab('rueckfragen')}
          className={`pb-2 text-sm font-medium flex items-center gap-1 ${tab === 'rueckfragen' ? 'text-[#9B1C1C] border-b-2 border-[#9B1C1C]' : 'text-gray-500'}`}>
          <MessageCircleQuestion className="w-4 h-4" /> Rückfragen &amp; Eskalation
          {rueckfragen.length > 0 && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full px-1.5">{rueckfragen.length}</span>}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">Fehler: {error}</div>
      )}

      {tab === 'uebersicht' && (
        <>
          <div className="flex items-center gap-3 flex-wrap bg-white rounded-xl border border-gray-200 px-4 py-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <select value={artFilter} onChange={e => setArtFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white">
              <option value="">Alle Arten</option>
              <option value="kunde">Kunde</option>
              <option value="honorar">Honorar</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={nurDringend} onChange={e => setNurDringend(e.target.checked)}
                className="rounded border-gray-300 text-[#9B1C1C]" />
              Nur dringend
            </label>
            <input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Suchen (Betreff, Nr., Gericht, Az)…"
              className="flex-1 min-w-[180px] text-sm border border-gray-200 rounded-lg px-3 py-1.5" />
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
              {PHASEN.map(p => <div key={p.id} className="h-40 bg-gray-100 rounded-xl animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 items-start">
              {PHASEN.map(p => {
                const items = byPhase(p.id);
                return (
                  <div key={p.id} className="bg-gray-50 rounded-xl border border-gray-200 p-2">
                    <div className="flex items-center justify-between px-1 py-1.5">
                      <span className="text-xs font-bold uppercase tracking-wide text-gray-600">{p.label}</span>
                      <span className="text-[10px] font-bold bg-white border border-gray-200 rounded-full px-1.5 text-gray-500">{items.length}</span>
                    </div>
                    <div className="space-y-2">
                      {items.map(r => (
                        <button key={r.id} onClick={() => setSelected(r)} className="w-full text-left bg-white rounded-lg border border-gray-200 p-2.5 space-y-1.5 hover:border-[#9B1C1C]/40 hover:shadow-sm transition cursor-pointer">
                          {r.stellungnahme_nr && (
                            <p className="text-[10px] font-mono text-gray-400">{r.stellungnahme_nr}</p>
                          )}
                          <p className="text-[12px] font-semibold text-gray-900 line-clamp-3">{r.betreff ?? 'Ohne Betreff'}</p>
                          {(r.gericht || r.gericht_aktenzeichen) && (
                            <p className="text-[11px] text-gray-500 flex items-center gap-1"><Gavel className="w-3 h-3" />{r.gericht} {r.gericht_aktenzeichen}</p>
                          )}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {r.prioritaet === 'dringend' && <span className="text-[10px] font-medium rounded px-1.5 py-0.5 bg-orange-100 text-orange-700 flex items-center gap-0.5"><Flame className="w-3 h-3" />dringend</span>}
                            {r.tage_bis_frist !== null && r.tage_bis_frist !== undefined && (
                              <span className={`text-[10px] font-medium rounded px-1.5 py-0.5 flex items-center gap-0.5 ${fristAmpel(r.tage_bis_frist)}`}>
                                <Clock className="w-3 h-3" />{r.tage_bis_frist < 0 ? `${Math.abs(r.tage_bis_frist)}T überfällig` : `${r.tage_bis_frist}T`}
                              </span>
                            )}
                            {r.eskalation && <span className="text-[10px] font-medium rounded px-1.5 py-0.5 bg-red-100 text-red-700 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />Eskalation</span>}
                            {r.rueckfrage_offen && <span className="text-[10px] font-medium rounded px-1.5 py-0.5 bg-amber-100 text-amber-700">Rückfrage</span>}
                            {(r.fehlablage_verdacht ?? 0) > 0 && <span className="text-[10px] font-medium rounded px-1.5 py-0.5 bg-rose-100 text-rose-700 flex items-center gap-0.5"><FileWarning className="w-3 h-3" />Fehlablage?</span>}
                            {r.freigabe_bereit && <span className="text-[10px] font-medium rounded px-1.5 py-0.5 bg-emerald-100 text-emerald-700 flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" />freigabereit</span>}
                          </div>
                          <div className="flex items-center justify-between">
                            {r.liegezeit_tage !== null && r.liegezeit_tage !== undefined && (
                              <p className="text-[10px] text-gray-400">Liegezeit {r.liegezeit_tage} Tage</p>
                            )}
                            {(r.kuerzungs_kandidaten ?? 0) > 0 && (
                              <p className="text-[10px] text-gray-400">{r.kuerzungs_kandidaten} Kürzungs-Dok.</p>
                            )}
                          </div>
                        </button>
                      ))}
                      {items.length === 0 && <p className="text-[11px] text-gray-300 px-1 py-2">—</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'rueckfragen' && (
        <div className="space-y-3">
          {rueckfragen.length === 0 ? (
            <div className="py-16 text-center">
              <MessageCircleQuestion className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-base font-semibold text-gray-400">Keine offenen Rückfragen</p>
            </div>
          ) : rueckfragen.map(r => (
            <div key={r.id} className="bg-white rounded-xl border border-amber-200 border-l-4 border-l-amber-500 px-4 py-3">
              <p className="text-sm font-semibold text-gray-900">{r.betreff ?? 'Ohne Betreff'}</p>
              {r.eskalation && r.eskalation_grund && <p className="text-xs text-red-600 mt-1">Eskalation: {r.eskalation_grund}</p>}
              {r.rueckfrage_text && <p className="text-xs text-gray-600 mt-1">{r.rueckfrage_text}</p>}
              {r.schriftsatzfrist && <p className="text-[11px] text-gray-400 mt-1">Frist: {new Date(r.schriftsatzfrist).toLocaleDateString('de-DE')}</p>}
            </div>
          ))}
        </div>
      )}

      {selected && <FreigabeDrawer sn={selected} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}
